import type { SceneJsonDto } from '@seddi/scene';
import { captureImage, captureImagePreview, type ImageType } from './actions/captureImage';
import { setCamera } from './actions/setCamera';
import { setSceneFromId, setSceneFromJson } from './actions/setScene';
import { store } from './store/appStore';
import { setResolution } from './store/appSlice';
import type { PerspectiveCamera } from './types/protocolTypes';
import { waitForAppReady, waitForSceneReady } from './appContext';
import { profileSync, profileAsync, setProfilerEnabled } from './profiler';

/**
 * Promise queue for waiting for render completion.
 * When a render completes (onUpdate fires), the next pending promise is resolved.
 * We use a sequence number to track when promises were added vs when renders complete.
 */
type PendingRender = { resolve: () => void; sequence: number };

const renderPromiseQueue: PendingRender[] = [];
let nextSequence = 0;

/**
 * Called by the viewer's onUpdate callback when rendering completes.
 * Resolves the next pending waitForRender promise.
 */
export const notifyRenderComplete = (): void => {
  // Resolve the next pending promise in the queue (FIFO)
  // This ensures we resolve promises in the order they were added
  if (renderPromiseQueue.length > 0) {
    const pending = renderPromiseQueue.shift();
    if (pending) {
      pending.resolve();
    }
  }
};

interface CaptureImagePreview {
  height: number;
  quality?: number;
  type?: ImageType;
  uploadURL: string;
  width: number;
}

interface CaptureImageOptions {
  preview?: CaptureImagePreview;
  quality?: number;
  type?: ImageType;
  uploadURL?: string;
}

type CaptureImageResult =
  | { type: 'buffer'; buffer: ArrayBuffer }
  | { type: 'upload'; promises: Promise<void>[] };

type SequenceCommand = { args?: unknown[]; method: string };

interface WindowEx extends Window {
  captureImage: (options?: CaptureImageOptions) => Promise<CaptureImageResult | false>;
  executeSequence: (commands: SequenceCommand[]) => Promise<unknown[]>;
  setCamera: (camera: PerspectiveCamera) => void;
  setProfilerEnabled: (enabled: boolean) => void;
  setResolution: (width: number, height: number) => void;
  setSceneFromId: (id: string) => void;
  setSceneFromJson: (scene: SceneJsonDto) => void;
  waitForAppReady: () => Promise<void>;
  waitForSceneReady: () => Promise<void>;
  waitForRender: () => Promise<void>;
}

declare let window: WindowEx;

/**
 * Expose functions to control the application from global window scope.
 */
export const init = () => {
  /**
   * Capture image.
   * @param options Options object with type, quality, and optional uploadURL.
   */
  window.captureImage = profileAsync(
    'captureImage',
    async (options?: CaptureImageOptions): Promise<CaptureImageResult | false> => {
      const viewerRef = store.getState().appRefs.viewerRef;

      if (!viewerRef?.current) return false;

      options ??= {};

      const blob = await captureImage(viewerRef.current, options.type, options.quality);

      if (!blob) return false;

      if (options.uploadURL) {
        // Upload image.
        const contentType = blob.type || 'image/png';
        const upload = fetch(options.uploadURL, {
          body: blob,
          headers: { 'Content-Type': contentType },
          method: 'PUT',
        }).then(response => {
          if (!response || !response.ok) {
            throw new Error(`Failed to upload image: ${response?.statusText || 'Unknown error'}`);
          }
        });
        const promises = [upload];

        // Upload preview.
        if (options.preview) {
          const {
            height,
            quality: previewQuality,
            type: previewType,
            uploadURL: previewUploadURL,
            width,
          } = options.preview;
          const previewBlob = await captureImagePreview(
            viewerRef.current,
            width,
            height,
            previewType,
            previewQuality,
          );
          const previewContentType = previewBlob.type || 'image/webp';
          const previewUpload = fetch(previewUploadURL, {
            body: previewBlob,
            headers: { 'Content-Type': previewContentType },
            method: 'PUT',
          }).then(response => {
            if (!response || !response.ok) {
              throw new Error(
                `Failed to upload preview: ${response?.statusText || 'Unknown error'}`,
              );
            }
          });
          promises.push(previewUpload);
        }

        return { type: 'upload', promises };
      } else {
        // Return the image buffer.
        const buffer = await blob.arrayBuffer();
        return { type: 'buffer', buffer };
      }
    },
  );

  /**
   * Set camera from camera desc.
   * @param camera Camera descriptor.
   */
  window.setCamera = profileSync('setCamera', (camera: PerspectiveCamera) => {
    const viewerRef = store.getState().appRefs.viewerRef;

    if (!viewerRef?.current) return;

    setCamera(viewerRef.current, camera);
  });

  /**
   * Set resolution.
   * @param width Width.
   * @param height Height.
   */
  window.setResolution = profileSync('setResolution', (width: number, height: number) => {
    store.dispatch(setResolution({ x: width, y: height }));
    // Note: Resolution change triggers React re-render, which will call viewer.setSize()
    // This happens asynchronously, so waitForRender() should be called after this
  });

  /**
   * Set scene from ID.
   * @param id Scene ID.
   */
  window.setSceneFromId = profileAsync('setSceneFromId', async (id: string) => {
    await setSceneFromId(store.dispatch, id);
  });

  /**
   * Set scene from JSON.
   * @param scene Scene JSON.
   */
  window.setSceneFromJson = profileAsync('setSceneFromJson', async (scene: SceneJsonDto) => {
    await setSceneFromJson(store.dispatch, scene);
  });

  /**
   * Returns a promise that resolves when the app is ready.
   */
  window.waitForAppReady = profileAsync('waitForAppReady', async () => {
    await waitForAppReady();
  });

  /**
   * Returns a promise that resolves when the scene is ready.
   */
  window.waitForSceneReady = profileAsync('waitForSceneReady', async () => {
    await waitForSceneReady();
  });

  /**
   * Wait for the next render to complete.
   * Call this after setCamera or setResolution to ensure the frame is painted before capturing.
   * This waits for the viewer's onUpdate callback, which fires after rendering is complete.
   *
   * For resolution changes, this also waits for React to finish re-rendering.
   */
  window.waitForRender = profileAsync('waitForRender', (): Promise<void> => {
    const viewerRef = store.getState().appRefs.viewerRef;

    // Ensure the viewer exists
    if (!viewerRef?.current) {
      return Promise.resolve();
    }

    const viewer = viewerRef.current;
    const sequence = nextSequence++;

    // For resolution changes, we need to wait for React to finish re-rendering
    // The resolution change triggers a React re-render, which then calls viewer.setSize()
    // We use requestAnimationFrame to ensure React has finished its render cycle
    return new Promise<void>(resolve => {
      // Wait for React to finish re-rendering (if resolution changed)
      // This ensures the Container component has updated
      requestAnimationFrame(() => {
        // Wait one more frame to ensure ResizeObserver has fired and viewer.setSize() has been called
        // This is necessary because ResizeObserver callbacks fire asynchronously
        requestAnimationFrame(() => {
          // Ensure a render is triggered by setting needsUpdate
          // This handles cases where setCamera/setResolution might not have triggered a render yet
          if (viewer.needsUpdate !== undefined) {
            viewer.needsUpdate = true;
          }

          // Add promise to queue with sequence number
          // This will be resolved when onUpdate fires after the next render
          renderPromiseQueue.push({ resolve, sequence });
        });
      });
    });
  });

  /**
   * Execute a sequence of window methods sequentially.
   * @param commands Array of commands with method name and arguments.
   * @returns Array of results from each method execution.
   */
  window.executeSequence = profileAsync(
    'executeSequence',
    async (commands: SequenceCommand[]): Promise<unknown[]> => {
      const results: unknown[] = [];
      const uploads: Promise<void>[] = [];

      for (const command of commands) {
        const method = window[command.method as keyof WindowEx];

        if (typeof method !== 'function') {
          throw new TypeError(`Method "${command.method}" is not a valid window method`);
        }

        const args = command.args ?? [];
        const result = await method(...args);

        // Handle command results
        switch (command.method) {
          case 'captureImage': {
            const imageResult = result as CaptureImageResult | false;
            if (imageResult) {
              switch (imageResult.type) {
                case 'buffer': {
                  results.push(imageResult.buffer);
                  break;
                }
                case 'upload': {
                  results.push(true);
                  uploads.push(...imageResult.promises);
                  break;
                }
              }
            }
            break;
          }
          default: {
            results.push(result);
          }
        }
      }

      // Wait for completion.
      if (uploads.length) {
        await waitForSequenceReady(uploads);
      }

      return results;
    },
  );

  /**
   * Enable or disable the profiler.
   */
  window.setProfilerEnabled = setProfilerEnabled;
};

const waitForSequenceReady = profileAsync('waitForSequenceReady', (promises: Promise<void>[]) => {
  return Promise.all(promises);
});
